/**
 * CaptionsEditorPageV2 — redesigned per-session caption editor.
 *
 * Layout (top → bottom):
 *   • PageHeader (eyebrow="Captions · {LANG}", title=session name,
 *     sub=source / duration / dirty state, actions=back-to-queue +
 *     CapStatus pill)
 *   • Sticky CaptionWaveform (wavesurfer.js v7 — brand-skinned)
 *   • Two-column body:
 *       LEFT  — cue list card (header bar + scrollable list of
 *               [timestamp button] + [Textarea] rows; active cue
 *               highlighted, auto-scrolled into view as audio plays)
 *       RIGHT — sidebar with:
 *               – Source picker (vertical card stack, only when
 *                 multiple providers exist for this language)
 *               – Last-error panel (only when status=failed)
 *               – Action stack (Approve, Save, Regenerate /
 *                 Re-translate, Translate, Delete)
 *               – Reviewer flags list
 *
 * All redux selectors / effects / handlers are preserved verbatim
 * from v1's CaptionsEditorPage.js — only the JSX (and the audio
 * surface) changed.
 *
 * Notable invariants preserved (per v1 comments — DO NOT regress):
 *   • `useState(() => urlSource || null)` lazy initializer for the
 *     source pick, with the effect ONLY re-checking when the current
 *     pick is no longer a valid candidate. Re-reading urlSource on
 *     every render would re-introduce the feedback loop bug where
 *     every toggle click snapped back to the URL's source.
 *   • The VTT download effect uses a `cancelled` flag so an older
 *     download can't settle after a newer one and overwrite the cues.
 *   • Switching source clears `dirty` so the Save button can't POST
 *     an empty VTT to a not-yet-generated provider row.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import {
  approveCaption,
  deleteCaption,
  downloadCaption,
  fetchSessionCaptions,
  recueCaption,
  regenerateCaption,
  saveCaption,
  translateSessionCaption,
  uploadCaption,
} from '../../../actions/captionsActions';
import { fetchOneSession } from '../../../actions/sessionActions';
import { parseVTT, serializeVTT } from '../../../utils/vttCodec';
import { formatTimeToMMSS } from '../../../utils/timeFormat';
import { prettySource } from '../../../constants/captionStyle';
import { flagDetailText } from '../../../constants/flagDetail';
import { PageHeader, Button, cn } from '../../redesign';
import { Textarea } from '../../redesign/edit';
import {
  CapStatus,
  FlagChip,
  CaptionWaveform,
} from '../../Captions/v2/captionsPrimitives';

/* ─── Inline icons ─────────────────────────────────────────────────── */

const BackIcon = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 18 9 12 15 6" />
  </svg>
);

const CheckIcon = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const SaveIcon = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
    <polyline points="17 21 17 13 7 13 7 21" />
    <polyline points="7 3 7 8 15 8" />
  </svg>
);

const RefreshIcon = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="23 4 23 10 17 10" />
    <polyline points="1 20 1 14 7 14" />
    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
  </svg>
);

const LanguageIcon = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <line x1="2" y1="12" x2="22" y2="12" />
    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
  </svg>
);

const TrashIcon = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6M14 11v6" />
    <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
  </svg>
);

const CloudDownloadIcon = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 16.58A5 5 0 0 0 18 7h-1.26A8 8 0 1 0 4 15.25" />
    <polyline points="8 17 12 21 16 17" />
    <line x1="12" y1="12" x2="12" y2="21" />
  </svg>
);

const CloudUploadIcon = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 16.58A5 5 0 0 0 18 7h-1.26A8 8 0 1 0 4 15.25" />
    <polyline points="16 16 12 12 8 16" />
    <line x1="12" y1="12" x2="12" y2="21" />
  </svg>
);

/* ─── Local SectionCard (eyebrow + body) ──────────────────────────── */
/* Local to the page; the eyebrow style matches V2's other section
 * cards (SearchPageV2's SectionCard, AppUserPageV2's V2CardWrap). */
function SectionCard({ title, accent, children, className }) {
  return (
    <section
      className={cn('bg-surface border border-sand', className)}
      style={{
        borderRadius: 12,
        padding: 16,
        overflow: 'hidden',
        borderLeft: accent ? `4px solid ${accent}` : undefined,
      }}
    >
      {title && (
        <div
          className="font-condensed font-bold uppercase text-text-muted"
          style={{
            fontSize: 11,
            letterSpacing: '0.08em',
            marginBottom: 12,
            color: accent || undefined,
          }}
        >
          {title}
        </div>
      )}
      {children}
    </section>
  );
}

/* ─── Source picker card (vertical button stack) ──────────────────── */
function SourcePickerCard({ candidates, source, onChange }) {
  return (
    <SectionCard title="Caption source">
      <p
        className="text-text-muted"
        style={{ fontSize: 11.5, marginTop: -6, marginBottom: 10, lineHeight: 1.45 }}
      >
        Multiple providers have a track for this language. Pick one to
        edit, approve, or compare.
      </p>
      <div className="flex flex-col" style={{ gap: 6 }}>
        {candidates.map((c) => {
          const active = c.source === source;
          return (
            <button
              key={c.source}
              type="button"
              onClick={() => onChange(c.source)}
              className={cn(
                'flex items-center justify-between text-left',
                'transition-colors duration-150',
              )}
              style={{
                padding: '8px 10px',
                borderRadius: 8,
                border: '1px solid ' + (active ? 'rgb(var(--color-teal-green))' : 'rgb(var(--color-sand))'),
                background: active ? 'var(--color-teal-green-soft)' : 'rgb(var(--color-cream))',
                color: 'rgb(var(--color-navy))',
                gap: 10,
                cursor: 'pointer',
              }}
            >
              <span style={{ fontSize: 13, fontWeight: 600 }}>
                {prettySource(c.source)}
              </span>
              <CapStatus status={c.status} />
            </button>
          );
        })}
      </div>
    </SectionCard>
  );
}

/* ─── Main page ───────────────────────────────────────────────────── */

export default function CaptionsEditorPageV2() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { sessionId: sessionIdParam, language } = useParams();
  const sessionId = parseInt(sessionIdParam, 10);
  const [searchParams] = useSearchParams();
  // Deep-link support: /captions/edit/:id/:lang?source=assemblyai opens
  // the editor with that source pre-selected. The review queue links
  // include this query param so clicking Edit on a specific row lands
  // on that row's source instead of the default.
  const urlSource = searchParams.get('source');
  const fileInputRef = useRef(null);
  const waveRef     = useRef(null);
  const cueListRef  = useRef(null);

  const captionsBySession = useSelector((s) => s.captions.bySession);
  const captions = captionsBySession[sessionId] ?? [];

  // A session+language can hold multiple provider rows (Whisper +
  // AssemblyAI). All rows matching the URL language are candidates;
  // `source` picks which one this editor is operating on.
  const candidates = useMemo(
    () => captions.filter((c) => c.language_code === language),
    [captions, language],
  );

  // Seed the picker from the URL `?source=` deep link ONCE at mount
  // via useState's lazy initializer. The effect below only handles
  // "current pick is no longer a valid candidate" (e.g. the operator
  // deleted the row) — it does NOT re-check urlSource on every render.
  // That used to create a feedback loop where every toggle click was
  // immediately snapped back to the URL's source.
  const [source, setSource] = useState(() => urlSource || null);
  useEffect(() => {
    if (candidates.length === 0) return;
    if (source && candidates.some((c) => c.source === source)) return;
    // No valid pick (initial mount with no urlSource, or the picked
    // source disappeared from the candidate list). Default to Whisper
    // when present, otherwise the first available row.
    const whisperRow = candidates.find((c) => c.source === 'whisper');
    setSource((whisperRow ?? candidates[0]).source);
  }, [candidates, source]);

  const caption = candidates.find((c) => c.source === source) ?? null;

  const [session, setSession] = useState(null);
  const [cues, setCues] = useState([]);
  const [vttLoaded, setVttLoaded] = useState(false);
  const [vttLoadError, setVttLoadError] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [activeCueId, setActiveCueId] = useState(null);

  // Fetch caption metadata + session detail in parallel on mount.
  useEffect(() => {
    dispatch(fetchSessionCaptions(sessionId));
    dispatch(fetchOneSession(sessionId))
      .then((res) => setSession(res?.data?.objects?.[0] ?? null))
      .catch(() => {});
  }, [dispatch, sessionId]);

  // Re-fetch and re-parse the VTT whenever the caption row's S3 URL or
  // last-updated timestamp changes (e.g. after a successful save or
  // regenerate). The `cancelled` flag guards against an older download
  // settling after a newer one — without it, the operator can upload a
  // new VTT (which triggers an updated_at bump → new fetch) while the
  // previous fetch is still in flight, and whichever resolves last
  // wins. Returning a cleanup that flips the flag ensures only the
  // latest effect updates state.
  useEffect(() => {
    if (!caption?.s3_url) {
      setVttLoaded(true);
      setCues([]);
      // This effect also runs on source-picker changes, not just
      // initial-mount + s3_url updates. If the operator was editing
      // one source (dirty=true) and switched to another that hasn't
      // generated yet, `dirty` would otherwise stay true with empty
      // cues — the Save button would enable and POST an empty VTT
      // to the new source. Clear dirty here so the picker switch is
      // a clean reset.
      setDirty(false);
      setVttLoadError(null);
      return undefined;
    }
    let cancelled = false;
    setVttLoaded(false);
    setVttLoadError(null);
    dispatch(downloadCaption(sessionId, language, source))
      .then(async (response) => {
        if (cancelled) return;
        const text =
          typeof response.data?.text === 'function'
            ? await response.data.text()
            : String(response.data || '');
        if (cancelled) return;
        const { cues: parsed, error } = parseVTT(text);
        if (error) {
          setVttLoadError(error);
          setCues([]);
        } else {
          setCues(parsed);
        }
        setDirty(false);
        setVttLoaded(true);
      })
      .catch((err) => {
        if (cancelled) return;
        setVttLoadError(err?.message || 'Failed to load VTT');
        setVttLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [dispatch, sessionId, language, source, caption?.s3_url, caption?.updated_at]);

  /* ─── Waveform → active cue (replaces v1's audio.onTimeUpdate) ── */
  /* CaptionWaveform invokes onTimeUpdate with the current second on
   * every wavesurfer audioprocess tick. We mirror v1's behavior:
   * find the cue covering [start, end), set it active, and scroll
   * it into view if it's off-screen in the cue list. */
  const onWaveTimeUpdate = useCallback(
    (t) => {
      const active = cues.find((c) => t >= c.start && t < c.end);
      if (active?.id !== activeCueId) {
        setActiveCueId(active?.id ?? null);
        if (active && cueListRef.current) {
          const el = cueListRef.current.querySelector(
            `[data-cue-id="${active.id}"]`,
          );
          if (el) {
            const parentRect = cueListRef.current.getBoundingClientRect();
            const elRect = el.getBoundingClientRect();
            const out =
              elRect.top < parentRect.top || elRect.bottom > parentRect.bottom;
            if (out) el.scrollIntoView({ block: 'center', behavior: 'smooth' });
          }
        }
      }
    },
    [cues, activeCueId],
  );

  const seekToCue = useCallback((cue) => {
    waveRef.current?.seek(cue.start);
  }, []);

  const onCueTextChange = (id, newText) => {
    setCues((prev) => prev.map((c) => (c.id === id ? { ...c, text: newText } : c)));
    setDirty(true);
  };

  /* ─── Actions ─────────────────────────────────────────────────── */

  const onApprove = () => {
    setSubmitting(true);
    dispatch(approveCaption(sessionId, language, undefined, source)).finally(() =>
      setSubmitting(false),
    );
  };

  const onSave = () => {
    setSubmitting(true);
    const vtt = serializeVTT(cues);
    dispatch(
      saveCaption(sessionId, language, vtt, () => setDirty(false), source),
    ).finally(() => setSubmitting(false));
  };

  // Regenerate ALWAYS dispatches AssemblyAI now — Kelly picked it as
  // the production winner. Existing Whisper rows from before the
  // switch are still viewable + approvable via the source picker,
  // but no path in the CMS triggers a NEW Whisper run.
  const onRegenerate = async () => {
    const existingAssembly = candidates.find((c) => c.source === 'assemblyai');
    const onlyHasWhisper = source === 'whisper' && !existingAssembly;
    if (onlyHasWhisper) {
      const { isConfirmed } = await Swal.fire({
        icon: 'question',
        title: 'Generate an AssemblyAI track?',
        text: 'This session only has a Whisper track. Queues an AssemblyAI run alongside; the Whisper row stays available until you delete it.',
        showCancelButton: true,
        confirmButtonText: 'Generate',
      });
      if (!isConfirmed) return;
    }
    setSubmitting(true);
    dispatch(
      regenerateCaption(
        sessionId,
        language,
        () => {
          // The new AssemblyAI row may not exist yet — give the queue
          // worker a beat before refetching so it appears as a source
          // card immediately.
          setTimeout(() => dispatch(fetchSessionCaptions(sessionId)), 1500);
        },
        { provider: 'assemblyai' },
      ),
    ).finally(() => setSubmitting(false));
  };

  // Re-cue: rebuild cues from the stored word sidecar with the latest
  // segmentation, no re-transcription. Only sensible on a not-yet-
  // reviewed transcribed track (the server enforces this too).
  const canRecue =
    caption?.status === 'auto_pending_review' &&
    (source === 'assemblyai' || source === 'whisper');
  const onRecue = async () => {
    const { isConfirmed } = await Swal.fire({
      icon: 'question',
      title: 'Re-cue from saved words?',
      text:
        'Rebuilds the cues from the stored word timings using the latest ' +
        'segmentation. No re-transcription — replaces the current (unreviewed) cues.',
      showCancelButton: true,
      confirmButtonText: 'Re-cue',
    });
    if (!isConfirmed) return;
    setSubmitting(true);
    dispatch(
      recueCaption(sessionId, language, source, () => {
        setTimeout(() => dispatch(fetchSessionCaptions(sessionId)), 1500);
      }),
    ).finally(() => setSubmitting(false));
  };

  // Translation trigger: only sensible on an approved EN caption.
  const canTranslate =
    language === 'en' &&
    (caption?.status === 'human_reviewed' || caption?.status === 'human_edited');
  const targetExists = !!captions.find((c) => c.language_code === 'es');
  const onTranslateToSpanish = async () => {
    const { isConfirmed } = await Swal.fire({
      icon: 'question',
      title: targetExists ? 'Re-translate to Spanish?' : 'Translate to Spanish?',
      text: targetExists
        ? 'An existing Spanish track will be reset to processing and replaced when the job completes.'
        : 'Queues a translation job. The Spanish track will appear in auto_pending_review when the job finishes.',
      showCancelButton: true,
      confirmButtonText: targetExists ? 'Re-translate' : 'Translate',
    });
    if (!isConfirmed) return;
    setSubmitting(true);
    dispatch(
      translateSessionCaption(sessionId, 'en', 'es', () => {
        Swal.fire({ icon: 'success', title: 'Queued', timer: 900 });
        setTimeout(() => dispatch(fetchSessionCaptions(sessionId)), 1500);
      }),
    ).finally(() => setSubmitting(false));
  };

  // Re-translate trigger for the OPPOSITE direction of canTranslate:
  // we're viewing a translated track (typically `es`, source=translated)
  // and want to re-run the translation from the approved English source.
  // Required for failed-translation rows because "Regenerate from audio"
  // is non-sensical on a row that has no audio relationship.
  const isTranslatedSource = source === 'translated';
  const englishSourceForRetranslate = captions.find(
    (c) =>
      c.language_code === 'en' &&
      (c.status === 'human_reviewed' || c.status === 'human_edited'),
  );
  const canReTranslate = isTranslatedSource && !!englishSourceForRetranslate;
  const onReTranslate = async () => {
    const { isConfirmed } = await Swal.fire({
      icon: 'question',
      title: 'Re-translate from English?',
      text: 'The current translated track will be reset to processing and replaced when the job completes.',
      showCancelButton: true,
      confirmButtonText: 'Re-translate',
    });
    if (!isConfirmed) return;
    setSubmitting(true);
    dispatch(
      translateSessionCaption(sessionId, 'en', language, () => {
        Swal.fire({ icon: 'success', title: 'Queued', timer: 900 });
        setTimeout(() => dispatch(fetchSessionCaptions(sessionId)), 1500);
      }),
    ).finally(() => setSubmitting(false));
  };

  const onDownload = async () => {
    try {
      const response = await dispatch(downloadCaption(sessionId, language, source));
      const url = URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      // Include the source in the filename so a comparison download
      // doesn't overwrite the prior provider's file on disk.
      const suffix =
        source && source !== 'whisper' && source !== 'manual'
          ? `.${source}`
          : '';
      a.download = `session-${sessionId}-${language}${suffix}.vtt`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (_e) {
      // errorHandler in the thunk already showed a toast.
    }
  };

  const onUploadClick = () => fileInputRef.current?.click();
  const onUploadChosen = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSubmitting(true);
    dispatch(uploadCaption(sessionId, language, file, undefined, source)).finally(
      () => {
        setSubmitting(false);
        e.target.value = '';
      },
    );
  };

  const onDelete = async () => {
    const { isConfirmed } = await Swal.fire({
      icon: 'warning',
      title: 'Delete this caption track?',
      html: `This deletes the <strong>${language.toUpperCase()}</strong> (<em>${prettySource(source)}</em>) track for session <code>#${sessionId}</code>.<br/>This cannot be undone.`,
      showCancelButton: true,
      confirmButtonText: 'Delete',
      confirmButtonColor: '#A5524B',
    });
    if (!isConfirmed) return;
    setSubmitting(true);
    dispatch(
      deleteCaption(
        sessionId,
        language,
        () => {
          // If another source row still exists for this language, stay
          // on the page so the operator can compare. Otherwise go back
          // to the queue.
          const otherSourceStillThere = candidates.some((c) => c.source !== source);
          if (!otherSourceStillThere) navigate('/captions/queue');
        },
        source,
      ),
    ).finally(() => setSubmitting(false));
  };

  const canApprove = caption?.status === 'auto_pending_review';

  const totalSec = useMemo(
    () => (cues.length > 0 ? cues[cues.length - 1].end : caption?.duration_sec ?? 0),
    [cues, caption],
  );

  const minutesLabel = session?.session_length_in_sec
    ? `${Math.round(session.session_length_in_sec / 60)} min`
    : null;

  /* ─── Header sub-line: source · duration · dirty/saved ──────── */
  const subParts = [];
  if (source) subParts.push(prettySource(source));
  if (minutesLabel) subParts.push(minutesLabel);
  if (dirty) subParts.push('unsaved changes');
  const subLine = subParts.join(' · ');

  /* ─── Render ────────────────────────────────────────────────── */
  return (
    <div className="bg-bg min-h-full">
      <PageHeader
        eyebrow={`Captions · ${(language || '').toUpperCase()}`}
        title={session?.session_name || `Session #${sessionId}`}
        sub={subLine || undefined}
        actions={
          <div className="flex items-center" style={{ gap: 10 }}>
            {caption?.status && <CapStatus status={caption.status} />}
            <Button
              variant="default"
              leadingIcon={BackIcon}
              onClick={() => navigate('/captions/queue')}
            >
              Back to queue
            </Button>
          </div>
        }
      />

      {/* Sticky waveform bar — sits below the header, stays visible
       *  while the operator scrolls through cues. */}
      <div
        className="bg-bg"
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 5,
          padding: '0 28px 14px',
          borderBottom: '1px solid rgb(var(--color-sand))',
        }}
      >
        <CaptionWaveform
          ref={waveRef}
          url={session?.session_main_url}
          onTimeUpdate={onWaveTimeUpdate}
        />
      </div>

      {/* Two-column body */}
      <div
        style={{
          padding: '20px 28px 60px',
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr) 340px',
          gap: 18,
          alignItems: 'start',
        }}
      >
        {/* LEFT — Cue list */}
        <SectionCard
          title={
            <span>
              Captions · {cues.length} cues · {Math.round(totalSec)}s
            </span>
          }
        >
          {/* Header note + Download/Upload actions */}
          <div
            className="flex items-start justify-between"
            style={{ gap: 12, marginTop: -4, marginBottom: 10, flexWrap: 'wrap' }}
          >
            <span
              className="text-text-muted"
              style={{ fontSize: 11.5, lineHeight: 1.45, maxWidth: 360 }}
            >
              Click a timestamp to seek the audio. The cue playing now is
              highlighted.
            </span>
            <div className="flex" style={{ gap: 6 }}>
              <Button
                size="sm"
                variant="default"
                leadingIcon={CloudDownloadIcon}
                onClick={onDownload}
              >
                Download .vtt
              </Button>
              <input
                type="file"
                accept=".vtt,text/vtt"
                ref={fileInputRef}
                onChange={onUploadChosen}
                style={{ display: 'none' }}
              />
              <Button
                size="sm"
                variant="default"
                leadingIcon={CloudUploadIcon}
                onClick={onUploadClick}
              >
                Upload .vtt
              </Button>
            </div>
          </div>

          {/* Cue list states */}
          {!caption ? (
            <div
              className="text-text-muted italic"
              style={{ padding: '24px 0', fontSize: 13 }}
            >
              Loading caption metadata…
            </div>
          ) : !caption.s3_url ? (
            <div
              className="bg-cream border border-sand"
              style={{
                borderRadius: 10,
                padding: '20px 16px',
                fontSize: 13,
                lineHeight: 1.5,
                color: 'rgb(var(--color-text))',
              }}
            >
              No VTT file is stored yet — click{' '}
              <strong>
                {isTranslatedSource
                  ? 'Re-translate from English'
                  : 'Regenerate from audio'}
              </strong>{' '}
              on the right.
            </div>
          ) : !vttLoaded ? (
            <div
              className="text-text-muted"
              style={{ padding: '20px 4px', fontSize: 12.5 }}
            >
              Fetching VTT…
            </div>
          ) : vttLoadError ? (
            <div
              className="text-coral-dark"
              style={{ padding: '16px 4px', fontSize: 12.5 }}
            >
              {vttLoadError}
            </div>
          ) : (
            <div
              ref={cueListRef}
              className="border border-sand"
              style={{
                borderRadius: 8,
                maxHeight: 'calc(100vh - 420px)',
                overflowY: 'auto',
                background: 'rgb(var(--color-cream))',
              }}
            >
              {cues.map((cue) => {
                const isActive = cue.id === activeCueId;
                return (
                  <div
                    key={cue.id}
                    data-cue-id={cue.id}
                    className="flex items-start"
                    style={{
                      gap: 10,
                      padding: '8px 10px',
                      borderBottom: '1px solid rgb(var(--color-sand))',
                      background: isActive ? 'var(--color-teal-green-soft)' : 'transparent',
                      borderLeft: isActive
                        ? '3px solid rgb(var(--color-teal-green))'
                        : '3px solid transparent',
                      transition: 'background-color 120ms ease',
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => seekToCue(cue)}
                      title={`Seek to ${formatTimeToMMSS(cue.start)}`}
                      className="font-mono cursor-pointer shrink-0"
                      style={{
                        minWidth: 56,
                        marginTop: 4,
                        padding: '3px 6px',
                        background: 'transparent',
                        border: '1px solid transparent',
                        borderRadius: 4,
                        color: isActive ? 'rgb(var(--color-teal-green-dark))' : 'rgb(var(--color-text-muted))',
                        fontSize: 11.5,
                        fontWeight: 600,
                        transition: 'all 120ms ease',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'rgb(var(--color-cream))';
                        e.currentTarget.style.borderColor = 'rgb(var(--color-sand))';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'transparent';
                        e.currentTarget.style.borderColor = 'transparent';
                      }}
                    >
                      {formatTimeToMMSS(cue.start)}
                    </button>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <Textarea
                        value={cue.text}
                        onChange={(e) => onCueTextChange(cue.id, e.target.value)}
                        style={{
                          minHeight: 36,
                          fontSize: 13,
                          padding: '6px 9px',
                          background: 'transparent',
                          border: '1px solid transparent',
                          resize: 'vertical',
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </SectionCard>

        {/* RIGHT — Sidebar */}
        <div className="flex flex-col" style={{ gap: 14 }}>
          {/* Source picker — only when multiple providers exist. */}
          {candidates.length > 1 && (
            <SourcePickerCard
              candidates={candidates}
              source={source}
              onChange={setSource}
            />
          )}

          {/* Last-error panel — only for failed rows. */}
          {caption?.last_error && caption?.status === 'failed' && (
            <SectionCard title="Last error" accent="rgb(var(--color-coral-dark))">
              <pre
                className="font-mono"
                style={{
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  fontSize: 12,
                  color: 'rgb(var(--color-text))',
                  margin: 0,
                  background: 'rgb(var(--color-cream))',
                  border: '1px solid rgb(var(--color-sand))',
                  borderRadius: 6,
                  padding: 10,
                  maxHeight: 200,
                  overflowY: 'auto',
                }}
              >
                {caption.last_error}
              </pre>
            </SectionCard>
          )}

          {/* Actions */}
          <SectionCard title="Actions">
            <div className="flex flex-col" style={{ gap: 8 }}>
              <Button
                variant="primary"
                leadingIcon={CheckIcon}
                onClick={onApprove}
                disabled={!canApprove || submitting || dirty}
                className="w-full"
                title={
                  canApprove
                    ? undefined
                    : 'Only auto_pending_review captions can be approved as-is.'
                }
              >
                Approve as-is
              </Button>
              <Button
                variant="coral"
                leadingIcon={SaveIcon}
                onClick={onSave}
                disabled={!vttLoaded || !dirty || submitting}
                className="w-full"
              >
                {dirty ? 'Save edits' : 'Saved'}
              </Button>
              {isTranslatedSource ? (
                <Button
                  variant="default"
                  leadingIcon={LanguageIcon}
                  onClick={onReTranslate}
                  disabled={!canReTranslate || submitting}
                  className="w-full"
                  title={
                    canReTranslate
                      ? undefined
                      : 'Need an approved English track to re-translate from. Approve the EN row first.'
                  }
                >
                  Re-translate from English
                </Button>
              ) : (
                <Button
                  variant="default"
                  leadingIcon={RefreshIcon}
                  onClick={onRegenerate}
                  disabled={submitting}
                  className="w-full"
                >
                  Regenerate from audio
                </Button>
              )}
              {canRecue && (
                <Button
                  variant="default"
                  onClick={onRecue}
                  disabled={submitting}
                  className="w-full"
                  title="Rebuild cues from the saved words (latest segmentation) — no re-transcription"
                >
                  Re-cue from saved words
                </Button>
              )}
              {canTranslate && (
                <Button
                  variant="default"
                  leadingIcon={LanguageIcon}
                  onClick={onTranslateToSpanish}
                  disabled={submitting}
                  className="w-full"
                >
                  {targetExists
                    ? 'Re-translate to Spanish'
                    : 'Translate to Spanish'}
                </Button>
              )}
              <div
                aria-hidden
                style={{ height: 1, background: 'rgb(var(--color-sand))', margin: '4px 0' }}
              />
              <Button
                variant="danger"
                leadingIcon={TrashIcon}
                onClick={onDelete}
                disabled={submitting}
                className="w-full"
              >
                Delete track
              </Button>
            </div>
          </SectionCard>

          {/* Reviewer flags */}
          <SectionCard title="Reviewer flags">
            {(caption?.flags ?? []).length === 0 ? (
              <p
                className="text-text-muted italic"
                style={{ fontSize: 12.5, margin: 0 }}
              >
                No flags raised by the post-processor.
              </p>
            ) : (
              <div className="flex flex-col" style={{ gap: 12 }}>
                {caption.flags.map((flag, idx) => {
                  const detail = flagDetailText(flag);
                  return (
                    <div
                      key={flag.id ?? idx}
                      style={{
                        paddingBottom: 10,
                        borderBottom:
                          idx < caption.flags.length - 1 ? '1px solid rgb(var(--color-sand))' : 'none',
                      }}
                    >
                      <div className="flex items-center" style={{ gap: 8, flexWrap: 'wrap' }}>
                        <FlagChip flag={flag} />
                        {flag.location_start_sec != null && (
                          <button
                            type="button"
                            onClick={() => seekToCue({ start: flag.location_start_sec })}
                            className="font-mono cursor-pointer"
                            style={{
                              padding: '2px 7px',
                              border: '1px solid rgb(var(--color-sand))',
                              borderRadius: 4,
                              background: 'rgb(var(--color-cream))',
                              color: 'rgb(var(--color-teal-green-dark))',
                              fontSize: 11,
                              fontWeight: 600,
                            }}
                          >
                            {formatTimeToMMSS(flag.location_start_sec)}
                          </button>
                        )}
                      </div>
                      {detail && (
                        <p
                          className="text-text-muted"
                          style={{ fontSize: 11.5, margin: '4px 0 0', lineHeight: 1.5 }}
                        >
                          {detail}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </SectionCard>
        </div>
      </div>
    </div>
  );
}
